import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, TextField, Button, Box, IconButton,
  List, ListItem, ListItemText, ListItemSecondaryAction,
  Alert, Chip, Avatar
} from '@mui/material';
import { Delete, PersonAdd, AdminPanelSettings, Email, Close } from '@mui/icons-material';
import { adminService } from '../services/api';

const AdminPanel = ({ onClose }) => {
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const loadUsers = async () => {
    try {
      const data = await adminService.getAllowedUsers();
      setEmails(data.allowedEmails || []);
    } catch (err) {
      setError('Failed to load users');
    }
  };

  useEffect(() => { loadUsers(); }, []);

  const handleAdd = async () => {
    if (!newEmail || !newEmail.includes('@')) {
      setError('Please enter a valid email');
      return;
    }
    setError('');
    setSuccess('');
    try {
      await adminService.addUser(newEmail);
      setNewEmail('');
      setSuccess(`${newEmail} added successfully`);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add email');
    }
  };

  const handleRemove = async (email) => {
    setError('');
    setSuccess('');
    try {
      await adminService.removeUser(email);
      setSuccess(`${email} removed`);
      loadUsers();
    } catch (err) {
      setError('Failed to remove email');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleAdd();
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '24px',
        background: 'rgba(255, 255, 255, 0.9)',
        border: '1px solid rgba(163, 51, 200, 0.1)',
        p: 4,
        mb: 4
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Avatar sx={{ mr: 2, background: 'linear-gradient(135deg, #3B1F5B 0%, #A333C8 100%)' }}>
          <AdminPanelSettings />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1E1E1E' }}>
            Access Control
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {emails.length === 0 ? 'No users authorized — all access blocked' : `${emails.length} authorized user(s)`}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            background: 'rgba(194, 24, 91, 0.1)',
            '&:hover': { background: 'rgba(194, 24, 91, 0.2)' }
          }}
        >
          <Close sx={{ color: '#C2185B' }} />
        </IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: '12px' }}>{success}</Alert>}

      <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search existing or enter new email..."
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          onKeyPress={handleKeyPress}
          size="small"
          InputProps={{
            startAdornment: <Email sx={{ color: '#7B68AE', mr: 1 }} />
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              '&.Mui-focused fieldset': { borderColor: '#A333C8' }
            }
          }}
        />
        <Button
          variant="contained"
          onClick={handleAdd}
          startIcon={<PersonAdd />}
          sx={{
            borderRadius: '12px',
            background: 'linear-gradient(45deg, #3B1F5B 30%, #A333C8 90%)',
            minWidth: 120,
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          Add
        </Button>
      </Box>

      {(() => {
        const filteredEmails = newEmail.trim()
          ? emails.filter(e => e.toLowerCase().includes(newEmail.toLowerCase()))
          : emails;

        if (emails.length === 0) {
          return (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No authorized users. Add emails above to grant access.
            </Typography>
          );
        }

        if (newEmail.trim() && filteredEmails.length === 0) {
          return (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No matching users found. Click Add to add this email.
            </Typography>
          );
        }

        return (
          <>
            {newEmail.trim() && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Showing {filteredEmails.length} of {emails.length} user(s)
              </Typography>
            )}
            <List sx={{ p: 0 }}>
              {filteredEmails.map((email) => (
                <ListItem
                  key={email}
                  sx={{
                    borderRadius: '12px',
                    mb: 1,
                    background: 'rgba(163, 51, 200, 0.05)',
                    border: '1px solid rgba(163, 51, 200, 0.1)'
                  }}
                >
                  <Chip
                    label={email}
                    sx={{
                      background: 'linear-gradient(45deg, #3B1F5B 30%, #A333C8 90%)',
                      color: '#fff',
                      fontWeight: 500,
                      borderRadius: '10px'
                    }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => handleRemove(email)}
                      sx={{ color: '#C2185B' }}
                    >
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </>
        );
      })()}
    </Paper>
  );
};

export default AdminPanel;
